/** QL-375 group 6 / QL-664 / QL-677 / QL-695 — the Apply mandate dialog. Read with `useT(T)`. */
export const T = {
  en_GB: {
    applyAmountHint:
      "A custom portfolio has no size of its own — it is the sum of its holdings — so the mandate needs an amount to spread across its weights.",
    applyAmountInvalid:
      "Enter an amount greater than zero, written as numbers are elsewhere in QP.",
    applyAmountLabel: "Amount to invest",
    applyAmountPerMonth: "Amount per month",
    applyAmountPerQuarter: "Amount per quarter",
    applyAmountPerYear: "Amount per year",
    applyApplied: "Mandate applied to the portfolio",
    applyApproxLine: (p: { instrument: string; weight: string; amount: string; target: string }) =>
      `${p.instrument} is sold in whole units, so ${p.weight} lands on the nearest unit — ${p.amount} rather than ${p.target}.`,
    applyApproxStillInside: "The portfolio is still inside the mandate.",
    applyApproxTitleMany: (count: string) => `${count} lines land approximately`,
    applyApproxTitleOne: "One line lands approximately",
    applyBreachBand: (p: { instrument: string; actual: string; min: string; max: string }) =>
      `${p.instrument} ${p.actual} (permitted ${p.min}–${p.max})`,
    applyBreachFixed: (p: { instrument: string; actual: string; weight: string }) =>
      `${p.instrument} ${p.actual} (mandate weight ${p.weight})`,
    applyButton: "Apply mandate",
    // QL-760 — the empty-portfolio template dialog builds the portfolio, so it says so.
    applyButtonTemplate: "Create from mandate",
    applyButtonSwitch: "Switch portfolio",
    applyButtonUpdate: "Update portfolio",
    applyChangeKept: "kept",
    applyColAmount: "Amount",
    applyColChange: "Change",
    applyColInstrument: "Instrument",
    applyColWeight: "Weight",
    applyDescription:
      "The portfolio is empty, so this fills it from the mandate's default allocation. Nothing is replaced.",
    applyDescriptionFollowup:
      "This sets every holding to the mandate's allocation for the amount below. Holdings the mandate does not name are sold in full; a held instrument that can no longer be bought is kept up to its current holding.",
    applyDescriptionReplace:
      "This replaces the portfolio's holdings with the mandate's allocation for the amount below.",
    applyDescriptionTemplate:
      "The portfolio is empty, so this fills it from the mandate's default allocation.",
    applyDescriptionModel:
      "A model portfolio cannot be rewritten. This switches it to a model portfolio on the goal's platform that matches the mandate line for line — the same instruments at the mandate's weights, whatever its risk score.",
    applyError: "Could not apply the mandate",
    applyExistingMandateOption: (name: string) => `Existing mandate: ${name}`,
    applyKeptLine: (p: { instrument: string; amount: string; reason: string }) =>
      `${p.instrument}: kept at ${p.amount} — ${p.reason}`,
    applyKeptTitleMany: (count: string) => `${count} holdings are kept, not bought`,
    applyKeptTitleOne: "One holding is kept, not bought",
    applyKeptCapped: (p: { created: string; requested: string }) =>
      `Only ${p.created} of ${p.requested} is invested: a kept holding cannot be bought up, so the other lines are sized to keep the mandate's weights.`,
    applyMandateLabel: "Mandate",
    applyMandateLines: (count: string) => `${count} instruments`,
    applyModelCurrent: "current",
    applyModelLabel: "Matching model portfolio",
    applyModelNone:
      "No model portfolio on the goal's platform matches the mandate — change the portfolio to custom or handle the deviation.",
    applyModelRiskScore: (score: string) => `risk score ${score}`,
    applyModelSwitched: "Portfolio switched to the matching model portfolio",
    applyMinBuyLine: (p: { instrument: string; amount: string; min: string }) =>
      `${p.instrument}: ${p.amount} is below the product's minimum of ${p.min}.`,
    applyMinBuyTitle: "Below the minimum purchase",
    applyNotCreated: "not created",
    applyNothingToCreate: "None of the mandate's lines can be created on this portfolio.",
    applyOverInvested: (p: { created: string; over: string; requested: string }) =>
      `Whole units are bought to the nearest unit, so this plan invests ${p.created} — ${p.over} more than the ${p.requested} entered.`,
    applyPreviewError: "Could not compute the allocation",
    applyReasonBelowMinBuy: (min: string) =>
      `below the product's minimum buy of ${min}, which this collection enforces`,
    applyReasonBelowOneUnit: "its share is less than half of one unit at the last price",
    applyReasonInactive: "product inactive",
    applyReasonNoIsin: "no ISIN, cannot be held in advice",
    applyReasonNoPrice: "no last price, so whole units cannot be sized",
    applyReasonNoProduct: "no product carries this ISIN",
    applyReasonNotOnPlatform: "not on the goal's platform",
    applyReasonUnmatchedHolding:
      "held at the bank as a position with no product — cannot be bought",
    applySoldDownLine: (p: { instrument: string; kept: string }) =>
      `${p.instrument}: sold down to ${p.kept} (held at the bank, cannot be bought)`,
    applyShortfallBreach:
      "A custom portfolio's weights are shares of its own total, so the created lines shift outside the mandate:",
    applyShortfallInvested: (p: { created: string; requested: string }) =>
      `Only ${p.created} of ${p.requested} will be invested.`,
    applyShortfallTitleMany: (count: string) => `${count} lines cannot be created`,
    applyShortfallTitleOne: "One line cannot be created",
    applySoldLine: (p: { instrument: string; amount: string }) => `${p.instrument}: ${p.amount}`,
    applySoldTitle: "Sold in full",
    applySoldDownTitle: "Sold down",
    applySoldMixedTitle: "Sold or sold down",
    applyTemplatePlaceholder: "Choose a mandate template",
    // QL-760 — the template dropdown's search and its two runs.
    applyTemplateSearch: "Search templates…",
    applyTemplateNoResults: "No template matches.",
    applyTemplateGroupMatching: "Matches the goal's risk score",
    applyTemplateGroupOther: "Other templates",
    applyTitle: "Apply mandate to this portfolio",
    applyTitleModel: "Update model portfolio from mandate",
    applyTitleReplace: "Update portfolio from mandate",
    applyTitleTemplate: "Create portfolio from mandate template",
    applyTotal: "Total",
    cancel: "Cancel",
  },
  nb_NO: {
    applyAmountHint:
      "En egendefinert portefølje har ingen egen størrelse — den er summen av beholdningene — så mandatet trenger et beløp å fordele etter vektene.",
    applyAmountInvalid: "Skriv et beløp større enn null, slik tall ellers skrives i QP.",
    applyAmountLabel: "Beløp å investere",
    applyAmountPerMonth: "Beløp per måned",
    applyAmountPerQuarter: "Beløp per kvartal",
    applyAmountPerYear: "Beløp per år",
    applyApplied: "Mandatet er brukt på porteføljen",
    applyApproxLine: (p: { instrument: string; weight: string; amount: string; target: string }) =>
      `${p.instrument} selges i hele andeler, så ${p.weight} lander på nærmeste andel — ${p.amount} i stedet for ${p.target}.`,
    applyApproxStillInside: "Porteføljen er fortsatt innenfor mandatet.",
    applyApproxTitleMany: (count: string) => `${count} linjer lander omtrentlig`,
    applyApproxTitleOne: "Én linje lander omtrentlig",
    applyBreachBand: (p: { instrument: string; actual: string; min: string; max: string }) =>
      `${p.instrument} ${p.actual} (tillatt ${p.min}–${p.max})`,
    applyBreachFixed: (p: { instrument: string; actual: string; weight: string }) =>
      `${p.instrument} ${p.actual} (mandatvekt ${p.weight})`,
    applyButton: "Bruk mandat",
    applyButtonTemplate: "Opprett fra mandat",
    applyButtonSwitch: "Bytt portefølje",
    applyButtonUpdate: "Oppdater porteføljen",
    applyChangeKept: "beholdes",
    applyColAmount: "Beløp",
    applyColChange: "Endring",
    applyColInstrument: "Instrument",
    applyColWeight: "Vekt",
    applyDescription:
      "Porteføljen er tom, så dette fyller den fra mandatets standardallokering. Ingenting erstattes.",
    applyDescriptionFollowup:
      "Dette setter hver beholdning til mandatets fordeling for beløpet under. Beholdninger mandatet ikke nevner, selges i sin helhet; et instrument kunden eier som ikke lenger kan kjøpes, beholdes inntil dagens beholdning.",
    applyDescriptionReplace:
      "Dette erstatter porteføljens beholdninger med mandatets allokering for beløpet under.",
    applyDescriptionTemplate:
      "Porteføljen er tom, så dette fyller den fra mandatets standardfordeling.",
    applyDescriptionModel:
      "En modellportefølje kan ikke skrives om. Dette bytter den til en modellportefølje på målets plattform som matcher mandatet linje for linje — de samme instrumentene med mandatets vekter, uansett risikoscore.",
    applyError: "Kunne ikke bruke mandatet",
    applyExistingMandateOption: (name: string) => `Eksisterende mandat: ${name}`,
    applyKeptLine: (p: { instrument: string; amount: string; reason: string }) =>
      `${p.instrument}: beholdes med ${p.amount} — ${p.reason}`,
    applyKeptTitleMany: (count: string) => `${count} beholdninger beholdes, kjøpes ikke`,
    applyKeptTitleOne: "Én beholdning beholdes, kjøpes ikke",
    applyKeptCapped: (p: { created: string; requested: string }) =>
      `Bare ${p.created} av ${p.requested} investeres: en beholdning som beholdes kan ikke kjøpes opp, så de andre linjene er dimensjonert for å holde mandatets vekter.`,
    applyMandateLabel: "Mandat",
    applyMandateLines: (count: string) => `${count} instrumenter`,
    applyModelCurrent: "nåværende",
    applyModelLabel: "Matchende modellportefølje",
    applyModelNone:
      "Ingen modellportefølje på målets plattform matcher mandatet — bytt til egendefinert portefølje eller håndter avviket.",
    applyModelRiskScore: (score: string) => `risikoscore ${score}`,
    applyModelSwitched: "Porteføljen er byttet til den matchende modellporteføljen",
    applyMinBuyLine: (p: { instrument: string; amount: string; min: string }) =>
      `${p.instrument}: ${p.amount} er under produktets minimum på ${p.min}.`,
    applyMinBuyTitle: "Under minste kjøp",
    applyNotCreated: "opprettes ikke",
    applyNothingToCreate: "Ingen av mandatets linjer kan opprettes på denne porteføljen.",
    applyOverInvested: (p: { created: string; over: string; requested: string }) =>
      `Hele andeler kjøpes til nærmeste andel, så denne planen investerer ${p.created} — ${p.over} mer enn de ${p.requested} som ble skrevet inn.`,
    applyPreviewError: "Kunne ikke beregne allokeringen",
    applyReasonBelowMinBuy: (min: string) =>
      `under produktets minstekjøp på ${min}, som denne samlingen håndhever`,
    applyReasonBelowOneUnit: "andelen er mindre enn en halv andel til sistekurs",
    applyReasonInactive: "produktet er inaktivt",
    applyReasonNoIsin: "ingen ISIN, kan ikke holdes i rådgivning",
    applyReasonNoPrice: "ingen sistekurs, så hele andeler kan ikke beregnes",
    applyReasonNoProduct: "ingen produkter har denne ISIN-en",
    applyReasonNotOnPlatform: "ikke på målets plattform",
    applyReasonUnmatchedHolding: "eid i banken som en posisjon uten produkt — kan ikke kjøpes",
    applySoldDownLine: (p: { instrument: string; kept: string }) =>
      `${p.instrument}: selges ned til ${p.kept} (eid i banken, kan ikke kjøpes)`,
    applyShortfallBreach:
      "Vektene i en egendefinert portefølje er andeler av dens egen total, så de opprettede linjene havner utenfor mandatet:",
    applyShortfallInvested: (p: { created: string; requested: string }) =>
      `Bare ${p.created} av ${p.requested} blir investert.`,
    applyShortfallTitleMany: (count: string) => `${count} linjer kan ikke opprettes`,
    applyShortfallTitleOne: "Én linje kan ikke opprettes",
    applySoldLine: (p: { instrument: string; amount: string }) => `${p.instrument}: ${p.amount}`,
    applySoldTitle: "Selges i sin helhet",
    applySoldDownTitle: "Selges ned",
    applySoldMixedTitle: "Selges eller selges ned",
    applyTemplatePlaceholder: "Velg en mandatmal",
    applyTemplateSearch: "Søk i maler…",
    applyTemplateNoResults: "Ingen mal passer.",
    applyTemplateGroupMatching: "Passer målets risikoscore",
    applyTemplateGroupOther: "Andre maler",
    applyTitle: "Bruk mandatet på denne porteføljen",
    applyTitleModel: "Oppdater modellporteføljen fra mandatet",
    applyTitleReplace: "Oppdater porteføljen fra mandatet",
    applyTitleTemplate: "Opprett portefølje fra mandatmal",
    applyTotal: "Totalt",
    cancel: "Avbryt",
  },
} as const;
